// Уведомления Hub: типы, иконки и подписи. Сами уведомления и настройки пользователя — на сервере (/api/notifications).
// Панель открывается колокольчиком в шапке окна, раздел «Уведомления» в настройках отвечает за то, куда и о чём присылать.
import { locale, t } from "@/lib/i18n";

export type NotificationKind = "export" | "message" | "broadcast" | "report" | "integration" | "team" | "workflow" | "channel" | "feedback";

export type NotificationGroup = "Сегодня" | "Вчера" | "Ранее";

export type AppNotification = {
  id: string;
  kind: NotificationKind;
  title: string;
  text: string;
  time: string;
  group: NotificationGroup;
  /** подпись ссылки внутри уведомления */
  action?: string;
  /** якорь окна, куда ведёт ссылка */
  href?: string;
  /** раздел настроек, который нужно открыть */
  tab?: string;
  entity?: string;
  entityId?: string;
  read: boolean;
  archived: boolean;
};

/** Уведомление в ответе API */
export type ServerNotification = {
  id: number;
  kind: NotificationKind;
  title: string;
  text: string;
  action?: string | null;
  href?: string | null;
  tab?: string | null;
  entity?: string | null;
  entityId?: string | null;
  read: boolean;
  archived: boolean;
  createdAt: string;
};

export const notificationKinds: Record<NotificationKind, { icon: string; label: string }> = {
  export: { icon: "ri-download-cloud-line", label: t("Экспорт данных") },
  message: { icon: "ri-chat-3-line", label: t("Диалог") },
  broadcast: { icon: "ri-mail-send-line", label: t("Рассылка") },
  report: { icon: "ri-bar-chart-2-line", label: t("Отчёт") },
  integration: { icon: "ri-links-line", label: t("Интеграция") },
  team: { icon: "ri-team-line", label: t("Команда") },
  workflow: { icon: "ri-flow-chart", label: t("Процесс") },
  channel: { icon: "ri-share-forward-line", label: t("Канал") },
  feedback: { icon: "ri-feedback-line", label: t("Замечание") },
};

const dayStart = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();

export function toAppNotification(n: ServerNotification, now = new Date()): AppNotification {
  const at = new Date(n.createdAt);
  const days = Math.round((dayStart(now) - dayStart(at)) / 86400000);
  const group: NotificationGroup = days <= 0 ? "Сегодня" : days === 1 ? "Вчера" : "Ранее";
  const hm = at.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" });
  const time = days <= 0 ? t("Сегодня в {hm}", { hm }) : days === 1 ? t("Вчера в {hm}", { hm }) : t("{date} в {hm}", { date: at.toLocaleDateString(locale, { day: "numeric", month: "long" }), hm });
  return {
    id: String(n.id), kind: n.kind in notificationKinds ? n.kind : "team", title: n.title, text: n.text, time, group,
    action: n.action ?? undefined, href: n.href ?? undefined, tab: n.tab ?? undefined, entity: n.entity ?? undefined, entityId: n.entityId ?? undefined,
    read: n.read, archived: n.archived,
  };
}

/* ── настройки уведомлений ── */

export type NotifyChannelId = "browser" | "email" | "telegram";
export type NotifyEventId = "dialogues" | "exports" | "broadcasts" | "workflows" | "integrations" | "team";

export type NotifyPrefs = {
  channels: Record<NotifyChannelId, boolean>;
  events: Record<NotifyEventId, boolean>;
  digest: string;
  quiet: { on: boolean; from: string; to: string };
  email?: string;
};

export const notifyChannels: { id: NotifyChannelId; name: string; icon: string; description: (email: string) => string }[] = [
  {
    id: "browser",
    name: t("Браузерные уведомления"),
    icon: "ri-notification-3-line",
    description: () => t("Всплывающее окно поверх вкладок, даже когда Hub свёрнут."),
  },
  {
    id: "email",
    name: t("Почта"),
    icon: "ri-mail-line",
    description: (email) => t("Письма на {email} — по выбранной частоте.", { email: email || t("вашу почту") }),
  },
  {
    id: "telegram",
    name: t("Telegram-бот"),
    icon: "ri-telegram-line",
    description: () => t("Короткие оповещения в личный чат с ботом рабочего пространства — заработает вместе с подключением Telegram."),
  },
];

export const notifyEvents: { id: NotifyEventId; label: string; description: string; icon: string }[] = [
  { id: "dialogues", label: t("Новые диалоги и упоминания"), description: t("Клиент написал впервые или коллега упомянул вас в заметке."), icon: "ri-chat-3-line" },
  { id: "exports", label: t("Экспорты и отчёты"), description: t("Файл выгрузки готов, недельный отчёт собран."), icon: "ri-download-cloud-line" },
  { id: "broadcasts", label: t("Рассылки"), description: t("Отправка завершена, доставка и ошибки по получателям."), icon: "ri-mail-send-line" },
  { id: "workflows", label: t("Рабочие процессы"), description: t("Процесс требует проверки или остановился на условии."), icon: "ri-flow-chart" },
  { id: "integrations", label: t("Интеграции"), description: t("Синхронизация завершилась или вернула ошибку."), icon: "ri-links-line" },
  { id: "team", label: t("Команда и замечания"), description: t("Вас добавили в команду, новое замечание «Идея / Проблема» или ответ по нему."), icon: "ri-team-line" },
];

export const notifyDigests = ["Сразу", "Раз в час", "Дайджест в 9:00"];

export const notifyDefaults: NotifyPrefs = {
  channels: { browser: false, email: true, telegram: false },
  events: { dialogues: true, exports: true, broadcasts: true, workflows: true, integrations: false, team: true },
  digest: "Сразу",
  quiet: { on: false, from: "22:00", to: "08:00" },
};
